// Generated by GitHub Copilot
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DirtyTrackedState } from '#core/state/dirty-tracked-state.ts'
import { useComponentState } from '#core/state/State.ts'
import { useUserState } from '#core/state/UserState.ts'
import RolesApi from '#core/api/auth/RolesApi.ts'
import RoleCreate from '#core/entities/auth/roles/RoleCreate.ts'
import type RoleDetail from '#core/entities/auth/roles/RoleDetail.ts'
import { Permission, PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import type { PermissionDependencyContext } from '#core/entities/auth/roles/permission-warnings.ts'
import { UpsertDrawerShell } from '#components/composites/upsert-drawer-shell.tsx'
import { FormField } from '#components/primitives/forms/form-field.tsx'
import { FormFooterActions } from '#components/primitives/components/form-footer-actions.tsx'
import { PermissionsMatrix, type LimitedSlotRequest } from '#components/domain/auth/auth/shared/permissions-matrix.tsx'
import { LimitedAccessEditorModal } from '#components/domain/auth/auth/shared/limited-access-editor-modal.tsx'
import {
  PermissionErrorAlert,
  permissionErrorBody,
  type PermissionErrorBody,
} from '#components/domain/auth/auth/shared/permission-error.tsx'
import { loadPermissionDependencyContext } from './role-dependency-context.ts'

const rolesApi = new RolesApi()

/** Maps role permissions to a `service → bitmask` lookup for fast toggle checks. */
function permissionsToMap(perms: Permission[]): Map<PermissionService, number> {
  const map = new Map<PermissionService, number>()
  for (const p of perms) {
    map.set(p.service, (map.get(p.service) ?? 0) | p.actions)
  }
  return map
}

/** Converts a `service → bitmask` map back to an array of Permission objects. */
function mapToPermissions(map: Map<PermissionService, number>): Permission[] {
  const result: Permission[] = []
  for (const [service, actions] of map) {
    if (actions === PermissionAction.NONE) continue
    const p = new Permission()
    p.service = service
    p.actions = actions
    result.push(p)
  }
  return result
}

/** Editing state for a single role's drawer: name + permission map, with dirty tracking. */
class RoleEditDrawerState extends DirtyTrackedState<RoleEditDrawerState> {
  roleId: string | null = null
  /** Default roles are viewable but never editable/deletable by company admins. */
  isDefault = false
  roleName = ''
  description = ''
  /** Reactive permission map stored as a serialisable record for the proxy layer. */
  permMap: Record<string, number> = {}
  isSaving = false
  limitedSlot: LimitedSlotRequest | null = null
  permError: PermissionErrorBody | null = null
  dependencyContext: PermissionDependencyContext = {}
  _dependenciesLoaded = false

  protected currentDirtySignature(): string {
    const perms = Object.entries(this.permMap)
      .filter(([, bits]) => bits !== PermissionAction.NONE)
      .sort(([a], [b]) => a.localeCompare(b))
    return JSON.stringify([this.roleName.trim(), this.description.trim(), perms])
  }

  /** Populates the form from an existing role (edit/view mode). */
  initFromRole(role: RoleDetail): void {
    this.clearDirtyBaseline()
    this.reactTo.roleId = role.id
    this.reactTo.isDefault = role.is_default
    this.reactTo.roleName = role.name
    this.reactTo.description = role.description ?? ''
    this.reactTo.permError = null
    this.reactTo.limitedSlot = null
    const record: Record<string, number> = {}
    for (const [k, v] of permissionsToMap(role.permissions)) record[k] = v
    this.reactTo.permMap = record
    void this.loadDependencies()
    this.captureDirtyBaseline()
  }

  /** Resets the form for create mode. */
  reset(): void {
    this.clearDirtyBaseline()
    this.reactTo.roleId = null
    this.reactTo.isDefault = false
    this.reactTo.roleName = ''
    this.reactTo.description = ''
    this.reactTo.permMap = {}
    this.reactTo.permError = null
    this.reactTo.limitedSlot = null
    void this.loadDependencies()
    this.captureDirtyBaseline()
  }

  /**
   * Loads (once per mount) the company form-settings + limited whitelists used to
   * flag dependency-tier warnings. Best-effort: never throws.
   */
  async loadDependencies(): Promise<void> {
    if (this._dependenciesLoaded) return
    this.reactTo._dependenciesLoaded = true
    this.reactTo.dependencyContext = await loadPermissionDependencyContext()
  }

  /** Replaces the whole permission map from a controlled `Permission[]` (matrix onChange). */
  setPermissionList(next: Permission[]): void {
    const record: Record<string, number> = {}
    for (const p of next) record[p.service] = p.actions
    this.reactTo.permMap = record
  }

  async save(onSaved: () => void): Promise<void> {
    if (this.isDefault) return
    this.reactTo.isSaving = true
    this.reactTo.permError = null
    try {
      const map = new Map<PermissionService, number>()
      for (const [k, v] of Object.entries(this.permMap)) map.set(k as PermissionService, v)
      const permissions = mapToPermissions(map)

      if (this.roleId) {
        await rolesApi.updateRole(this.roleId, { name: this.roleName, description: this.description.trim(), permissions })
      } else {
        const data = new RoleCreate()
        data.name = this.roleName
        data.description = this.description.trim()
        data.permissions = permissions
        await rolesApi.createRole(data)
      }
      onSaved()
    } catch (e) {
      // A permission-feature-flags 422 (disallowed permission / cap exceeded) carries a
      // machine-readable body we render inline; anything else bubbles to the drawer error.
      const permError = permissionErrorBody(e)
      if (permError) {
        this.reactTo.permError = permError
      } else {
        throw e
      }
    } finally {
      this.reactTo.isSaving = false
    }
  }
}

interface RoleEditDrawerProps {
  open: boolean
  /** Role to edit, or null for create mode. */
  roleId: string | null
  roles: RoleDetail[]
  onClose: () => void
  onSaved: () => void
}

/** Right-side drawer for creating, editing, or viewing a role's permissions. */
export function RoleEditDrawer({ open, roleId, roles, onClose, onSaved }: RoleEditDrawerProps) {
  const { t } = useTranslation('settings')
  const state = useComponentState(RoleEditDrawerState)
  const userState = useUserState()

  useEffect(() => {
    if (!open) return
    const role = roleId ? roles.find(r => r.id === roleId) : null
    if (role) state.initFromRole(role)
    else state.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roleId])

  const isEdit = state.roleId != null
  const title = isEdit
    ? (state.isDefault ? t('usersTeams.roleDrawer.viewTitle') : t('usersTeams.roleDrawer.editTitle'))
    : t('usersTeams.actions.addRole')

  const canSave = isEdit
    ? userState.hasPermission(PermissionService.PERMISSIONS, PermissionAction.UPDATE)
    : userState.hasPermission(PermissionService.PERMISSIONS, PermissionAction.CREATE)

  const permissionList: Permission[] = Object.entries(state.permMap)
    .filter(([, bits]) => bits !== PermissionAction.NONE)
    .map(([service, bits]) => {
      const p = new Permission()
      p.service = service as PermissionService
      p.actions = bits
      return p
    })

  const handleSave = () => {
    void state.save(onSaved).catch(() => { state.reactTo.permError = null })
  }

  return (
    <UpsertDrawerShell
      isOpen={open}
      title={title}
      onClose={onClose}
      drawerWidth="min(100vw, 56rem)"
      hasUnsavedChanges={state.isDirty && !state.isSaving && !state.isDefault}
      // Escape should close the limited-access editor stacked over the drawer, not the drawer.
      closeOnEscape={!state.limitedSlot}
      footer={(requestClose) => state.isDefault ? (
        <div className="d-flex justify-content-end w-100">
          <button type="button" className="btn btn-outline-secondary" onClick={requestClose}>
            {t('shared.actions.close')}
          </button>
        </div>
      ) : (
        <FormFooterActions
          onCancel={requestClose}
          onSubmit={handleSave}
          submitting={state.isSaving}
          submitLabel={isEdit ? t('shared.actions.save') : t('shared.actions.create')}
          submitDisabled={!state.roleName.trim() || !canSave}
          layout="row"
        />
      )}
    >
      {state.isDefault && (
        <div className="alert alert-info py-2">{t('usersTeams.rolesModal.defaultReadOnlyNote')}</div>
      )}

      <FormField label={t('shared.fields.roleName')} required={!state.isDefault}>
        <input
          type="text"
          className="form-control"
          value={state.roleName}
          readOnly={state.isDefault}
          onChange={e => { state.reactTo.roleName = e.target.value }}
        />
      </FormField>

      <FormField label={t('shared.fields.description')}>
        <textarea
          className="form-control"
          rows={2}
          value={state.description}
          readOnly={state.isDefault}
          onChange={e => { state.reactTo.description = e.target.value }}
        />
      </FormField>

      <div className="mt-3">
        <PermissionsMatrix
          value={permissionList}
          onChange={next => state.setPermissionList(next)}
          readOnly={state.isDefault}
          control="switch"
          columns={4}
          header={{ area: t('usersTeams.rolesModal.area') }}
          bulkActions={{ enableAll: t('usersTeams.rolesModal.enableAll'), clearAll: t('usersTeams.rolesModal.clearAll') }}
          categoryLabel={(key) => t(`permissions.categories.${key}`, { ns: 'general' })}
          actionLabel={(label) => t(`permissions.actions.${label}`, { ns: 'general' })}
          onConfigureLimited={(request) => { state.reactTo.limitedSlot = request }}
          dependencyContext={state.dependencyContext}
        />
      </div>

      {state.permError && <div className="mt-3"><PermissionErrorAlert body={state.permError} /></div>}

      {/* Company-wide limited-access whitelist editor (keyed so the state reloads per slot). */}
      {state.limitedSlot && (
        <LimitedAccessEditorModal
          key={`${state.limitedSlot.service}-${state.limitedSlot.action}`}
          request={state.limitedSlot}
          onClose={() => { state.reactTo.limitedSlot = null }}
        />
      )}
    </UpsertDrawerShell>
  )
}
